import { type Component, defineAsyncComponent } from 'vue'

import {
  PaymentIntegrationType,
  PaymentMethodCode,
  PaymentServiceProvider,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'

import AdyenCardAdapter from '../../form-adyen/components/AdyenCardAdapter/AdyenCardAdapter.vue'
import AdyenFakeAdapter from '../../form-adyen/components/AdyenFakeAdapter/AdyenFakeAdapter.vue'
import AdyenGooglePayAdapter from '../../form-adyen/components/AdyenGooglePayAdapter/AdyenGooglePayAdapter.vue'
import AdyenHostedAdapter from '../../form-adyen/components/AdyenHostedAdapter/AdyenHostedAdapter.vue'
import AdyenKonbiniAdapter from '../../form-adyen/components/AdyenKonbiniAdapter/AdyenKonbiniAdapter.vue'
import AdyenMbwayAdapter from '../../form-adyen/components/AdyenMbwayAdapter/AdyenMbwayAdapter.vue'
import ApplePayAdapter from '../../form-apple-pay/components/ApplePayAdapter/ApplePayAdapter.vue'
import type { PaymentFormAdapterProps } from '../../form-common/types'
import ProcessOutCardAdapter from '../../form-processout/components/ProcessOutCardAdapter/ProcessOutCardAdapter.vue'
// TODO: [PAYIN-3548] The adapter could be lazy-loaded to avoid loading all the adapters at once.
// Especially when adapters are country-specific.
import PassThroughAdapter from '../components/PassThroughAdapter/PassThroughAdapter.vue'

// Lazy load back-office payment methods to prevent loading it on the front-office
const AchAdapter = defineAsyncComponent(
  () => import('../../form-buyback/components/AchAdapter/AchAdapter.vue'),
)
const BacsAdapter = defineAsyncComponent(
  () => import('../../form-buyback/components/BacsAdapter/BacsAdapter.vue'),
)
const SepaAdapter = defineAsyncComponent(
  () => import('../../form-buyback/components/SepaAdapter/SepaAdapter.vue'),
)

// TODO, replace each adapter by the real one, once they are implemented
const CompatibilityAdapter = PassThroughAdapter
const ProcessOutHostedAdapter = PassThroughAdapter

type Adapter = Component<PaymentFormAdapterProps>
type ConfigLevel1 = Record<PaymentServiceProvider, ConfigLevel2>
type ConfigLevel2 = Partial<Record<PaymentMethodCode, ConfigLevel3>> & {
  DEFAULT: ConfigLevel3
}
type ConfigLevel3 = Partial<Record<PaymentIntegrationType, Adapter>> & {
  DEFAULT: Adapter
}

const ADYEN_COMMON = {
  [PaymentMethodCode.CARD]: {
    [PaymentIntegrationType.ADYEN_CHECKOUT_API]: AdyenCardAdapter,
    DEFAULT: CompatibilityAdapter,
  },
  [PaymentMethodCode.APPLE_PAY]: {
    DEFAULT: ApplePayAdapter,
  },
  [PaymentMethodCode.GOOGLE_PAY]: {
    DEFAULT: AdyenGooglePayAdapter,
  },
  [PaymentMethodCode.KONBINI_GENERIC]: {
    DEFAULT: AdyenKonbiniAdapter,
  },
  [PaymentMethodCode.MBWAY]: {
    DEFAULT: AdyenMbwayAdapter,
  },
  [PaymentMethodCode.FAKE]: {
    DEFAULT: AdyenFakeAdapter,
  },
  [PaymentMethodCode.ACH]: {
    DEFAULT: AchAdapter,
  },
  [PaymentMethodCode.BACS]: {
    DEFAULT: BacsAdapter,
  },
  [PaymentMethodCode.SEPA]: {
    DEFAULT: SepaAdapter,
  },
  DEFAULT: {
    [PaymentIntegrationType.ADYEN_CHECKOUT_API]: AdyenHostedAdapter,
    DEFAULT: PassThroughAdapter,
  },
}

export const PAYMENT_FORM_ADAPTER_NOT_REQUIRED = PassThroughAdapter

export const PAYMENT_FORM_ADAPTERS: ConfigLevel1 = {
  [PaymentServiceProvider.ADYEN_PAYIN_PAYOUT]: ADYEN_COMMON,
  [PaymentServiceProvider.ADYEN_MARKETPAY]: ADYEN_COMMON,

  [PaymentServiceProvider.PAYPAL]: {
    DEFAULT: {
      DEFAULT: PassThroughAdapter,
    },
  },

  [PaymentServiceProvider.PROCESSOUT]: {
    [PaymentMethodCode.CARD]: {
      DEFAULT: ProcessOutCardAdapter,
    },
    [PaymentMethodCode.APPLE_PAY]: {
      DEFAULT: ApplePayAdapter,
    },
    DEFAULT: {
      DEFAULT: ProcessOutHostedAdapter,
    },
  },
}
